import { IonIcon, IonItem, useIonRouter } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { type Status, StatusBadge } from "@/components/atoms/status-badge";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/backend";
import { type Application } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { useRevokePeriod } from "@/lib/backend/utils/hooks/use-revoke-period";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { queryClient } from "@/lib/providers/query";
import { Routes } from "@/lib/router";
import { ShiftApplicationDetails } from "../shift-application/shift-application-details";

export function ApplicationCardSimple({
  application,
  navigateToProject = false,
}: {
  application: Application;
  navigateToProject?: boolean;
}) {
  const { t } = useTranslation();
  const router = useIonRouter();
  const modalRevoke = useRef<HTMLIonModalElement>(null);

  const { isWithinRevokePeriod } = useRevokePeriod();

  const revokeApplicationMutation = useExtractedMutation({
    mutationFn: api.application.revokeEmployeeApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["applications"],
      });
    },
    onError: () => toast.error(t("actions:revoke.error")),
  });

  const startAt =
    application.availabilitySlot?.startAt && new Date(application.availabilitySlot.startAt);
  const formattedDate = startAt?.toLocaleString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const formattedTime = startAt?.toLocaleString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const endTime =
    application.availabilitySlot?.endAt &&
    new Date(application.availabilitySlot.endAt).toLocaleString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const canRevoke = isWithinRevokePeriod(startAt);

  const handleRevoke = async () => {
    try {
      await revokeApplicationMutation.mutateAsync({
        id: application.id,
      });
      modalRevoke.current?.dismiss();
    } catch (error) {
      toast.error(t("actions:revoke.error"));
    }
  };

  const handleClick = () => {
    if (navigateToProject && application.availabilitySlot?.project?.id) {
      router.push(Routes.ProjectShifts({ id: application.availabilitySlot.project.id }));
    }
  };

  return (
    <>
      <IonItem
        className="ion-no-padding w-full rounded-2xl bg-surface-primary text-md [--border-width:0] [--padding-end:0] [--padding-start:0]"
        lines="none"
        style={{ "--background": "var(--ion-color-surface-primary)" }}
        onClick={handleClick}
        button={navigateToProject}
      >
        <Card className="w-full overflow-hidden border-none bg-surface-primary shadow-none">
          <CardContent className="p-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between">
                <h3 className="font-bold capitalize text-txt-brand">{formattedDate}</h3>
                <StatusBadge status={application.status as Status} />
              </div>

              <ShiftApplicationDetails
                label={t("entity:user-function.singular.capitalized")}
                labelValue={
                  application.availabilitySlot?.userFunction?.name ?? t("common:all-functions")
                }
                startTime={formattedTime ?? ""}
                endTime={endTime ?? ""}
              />

              {t(`enums:applicationStatus.${application.status}`)}

              {canRevoke ? (
                <div className="mt-2 flex flex-col gap-2">
                  <Button
                    variant="secondary"
                    size="xl"
                    className="w-full"
                    onClick={() => modalRevoke.current?.present()}
                  >
                    {t("actions:revoke.action")}
                  </Button>
                </div>
              ) : (
                <p className="text-xsm text-txt-secondary">
                  {t("actions:revoke.disabled.exceeded-revoke-period")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </IonItem>

      <ModalSheetAutoHeight
        ref={modalRevoke}
        onWillDismiss={async (event) => {
          if (event.detail.role === "confirm") {
            await handleRevoke();
          }
        }}
      >
        <div className="ion-padding flex flex-col gap-6 pt-6 text-md">
          <div className="flex items-center justify-between">
            <strong className="text-center text-xl text-primary">{t("modals:revoke.title")}</strong>
            <button
              onClick={() => modalRevoke.current?.dismiss()}
              className="p-2 text-txt-secondary hover:text-txt-primary"
            >
              <IonIcon icon={xMarkIcon} className="size-6" />
            </button>
          </div>

          <p className="text-txt-secondary">{t("modals:revoke.description")}</p>

          <div className="h- flex flex-col gap-2">
            <Button
              className="text-md font-bold"
              size="xl"
              onClick={() => modalRevoke.current?.dismiss(true, "confirm")}
            >
              {t("modals:revoke.confirm")}
            </Button>
          </div>
        </div>
      </ModalSheetAutoHeight>
    </>
  );
}
